import { API_BASE_URL } from "../config";
import { getAccessToken } from "./auth-helper";

export type SignatureInsertionMode = "officeJs" | "manualMerge";

export interface Capabilities {
  plan: string;
  subscriptionStatus?: string | null;
  isLicensed?: boolean;
  seatBlocked?: boolean;
  canEditOrgTemplates?: boolean;
  canAccessAdminPortal?: boolean;
  /**
   * Set when the admin group membership could not be checked at all
   * ("graph_consent_required" | "graph_unavailable") — not the same as "not an admin".
   */
  adminAccessError?: string | null;
  canUseSignatures?: boolean;
  showWatermark?: boolean;
  marketplaceManageUrl?: string | null;
  /**
   * Product-fixed primary mode is officeJs (Signatur-Slot).
   * manualMerge is only used as automatic insert fallback (over limit / API failure).
   */
  signatureInsertionMode?: string | null;
  /** Allow http(s) image URLs. Product-fixed: true. */
  allowImageUrl?: boolean;
  /** Allow base64 (data:) images. Product-fixed: false. */
  allowImageBase64?: boolean;
  /** Allow Outlook inline attachments via blob assets. Product-fixed: true. */
  allowImageAsAttachment?: boolean;
  /** Approved formats: png, jpeg, gif, webp. */
  allowedAttachmentFormats?: string[] | null;
}

/** Always officeJs — Body-Einfügen (manualMerge) is insert-time fallback only. */
export function getSignatureInsertionMode(
  _caps?: Capabilities | null | undefined
): SignatureInsertionMode {
  return "officeJs";
}

/** Product-fixed: URL images enabled. */
export function isImageUrlAllowed(_caps?: Capabilities | null | undefined): boolean {
  return true;
}

/** Product-fixed: Base64 images disabled. */
export function isImageBase64Allowed(_caps?: Capabilities | null | undefined): boolean {
  return false;
}

/** Product-fixed: cid/attachment images enabled. */
export function isImageAttachmentAllowed(_caps?: Capabilities | null | undefined): boolean {
  return true;
}

export function getAllowedAttachmentFormats(_caps?: Capabilities | null | undefined): string[] {
  return ["png", "jpeg", "gif", "webp"];
}

export async function fetchCapabilities(token?: string | null): Promise<Capabilities | null> {
  const accessToken = token ?? getAccessToken();
  if (!accessToken) return null;

  const res = await fetch(`${API_BASE_URL}/api/me/capabilities`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
    credentials: "omit",
  });

  if (res.status === 401) return null;
  if (!res.ok) return null;
  return (await res.json()) as Capabilities;
}

export function formatPlanLabel(plan: string): string {
  const normalized = plan.trim().toLowerCase();
  if (normalized === "pro") return "PRO";
  if (normalized === "free") return "Free";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

const WATERMARK_HTML =
  '<div style="text-align:right;font-size:8pt;color:#666666;margin-top:6px;">Powered by SunnySign</div>';

export function shouldShowWatermark(
  caps: Capabilities | null | undefined,
  planFallback?: "free" | "pro" | null
): boolean {
  if (caps?.showWatermark === true) return true;
  if (caps?.showWatermark === false) return false;
  const plan = caps?.plan?.toLowerCase() ?? planFallback ?? "free";
  return plan !== "pro";
}

export function appendWatermarkIfNeeded(
  html: string,
  caps: Capabilities | null | undefined,
  planFallback?: "free" | "pro" | null
): string {
  return shouldShowWatermark(caps, planFallback) ? html + WATERMARK_HTML : html;
}
